import React, { useEffect, useState, useMemo } from "react";
import { injectIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import ScenarioBar from "../../components/ScenarioBar";
import { ROUTES, SCENARIOS, QUIZZES } from "../../utils/constants";
import { getScenarioNumByPath, getTimer, shuffleQuizOptions } from "../../utils/helpers";
import { updateTime } from "../../redux/actions";
import { postAnswer } from "../../api/answer";
import {
  modalConfirm,
  modalQuestionResolution,
  modalQuestionExplanation,
  modalHint,
  modalHintShow
} from "../../utils/logger";
import { buildQuizFeedbackBlockHtml, buildQuizResultAvatarHtml } from "../../utils/quizFeedbackHtml";
import { IconHandClick } from "@tabler/icons-react";
import CheckButton from "../../components/CheckButton";
import estasseguro from "../../assets/bg/estasseguro-avatar-violet.png";
import respuestacorrecta from "../../assets/bg/correcto-avatar-pre.png";
import respuestaincorrecta from "../../assets/bg/incorrecto-avatar-pre.png";
import btnPistaViolet from "../../assets/btnPistaViolet.png"
import imageHint from "../../assets/hints/imageHintViolet.png"
import imageHintUsed from "../../assets/btnPistaViolet.png"
import unIntento from "../../assets/un-intento-violet.png"
import dosIntentos from "../../assets/dos-intentos-violet.png"
import imgPistaQuiz4 from "../../assets/hints/hint-pregunta4.png";

const SCENARIO3_QUIZ1_BASE_OPTIONS = [
	{
		id: 1,
		text: "Realización de PET-TC con PSMA.",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 2,
		text: "Biopsia prostática para estudio histológico.",
		correct: true,
		img: null,
		explanation: "",
	},
	{
		id: 3,
		text: "Realización de PET-TC con colina.",
		correct: false,
		img: null,
		explanation: "",
	},
	{
		id: 4,
		text: "Cribado de marcadores oncológicos adicionales.",
		correct: false,
		img: null,
		explanation: "",
	},
];

function Scenario3Quiz1({ intl }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();
  const group = useSelector((state) => state.group);

  const activeScenario = getScenarioNumByPath(location.pathname);

  const QUIZ_ID = QUIZZES.QUIZ4;
  const SCENARIO_ID = SCENARIOS.SCENARIO_3;

  const [selectedOption, setSelectedOption] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);
  const [hintUsed, setHintUsed] = useState(false);
  const [hintPenalty, setHintPenalty] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [attempt, setAttempt] = useState(1);
  const [blockedOptions, setBlockedOptions] = useState(new Set());
  const [pendingScore, setPendingScore] = useState(() => Number(group?.score || 0));
  const [hintAppliedToScore, setHintAppliedToScore] = useState(false);
  const [seededBaseScore, setSeededBaseScore] = useState(false);

  useEffect(() => {
    if (seededBaseScore) return;
    if (!group) return;
    const s = Number(group?.score);
    if (!Number.isFinite(s)) return;
    setPendingScore(s);
    setSeededBaseScore(true);
  }, [group, seededBaseScore]);
  
  const NUMBERATTEMPTS = 2;
  const CORRECT_ANSWER_POINTS = 10;
  const WRONG_ANSWER_POINTS = -5;
  const CORRECT_ANSWER_POINTS_2 = 3;
  const WRONG_ANSWER_POINTS_2 = 0;

  const retroalimentacion = "Aunque la PET-TC con PSMA aporta un alto rendimiento para la estadificación y puede reforzar de forma muy consistente la sospecha clínica de cáncer de próstata metastásico, la prueba imprescindible para establecer el diagnóstico definitivo sigue siendo la confirmación histopatológica, habitualmente mediante biopsia prostática. En este caso, es la anatomía patológica la que permite integrar de forma concluyente el cuadro clínico, analítico y radiológico, y sustentar el inicio de la estrategia terapéutica específica.";
  const retroalimentacionBlockHtml = buildQuizFeedbackBlockHtml(retroalimentacion, "violet");

  const displayOptions = useMemo(() => shuffleQuizOptions(SCENARIO3_QUIZ1_BASE_OPTIONS), []);

  async function submitAnswer(finalScore, correct) {
    const totalTime = getTimer();
    dispatch(updateTime(totalTime, 1));

    try {
      const res = await postAnswer({
        scenario: SCENARIO_ID,
        quiz: QUIZ_ID,
        group: group?._id,
        score: finalScore,
        time: totalTime,
        lastVisited: ROUTES.SCENARIO3_FIN,
        correct
      });

      if (res.status === 201) {
        const serverGroup = res.data?.group;
        if (serverGroup) {
          // usa tu action real para setear el group; si no tienes, un dispatch plano:
          dispatch({ type: "GROUP_SET", payload: serverGroup });
        }
       
        setIsCompleted(true);

        // Seguimos el flujo normal
        navigate(ROUTES.SCENARIO3_FIN);
      }
    } catch (error) {
      const status = error?.response?.status;
      const code = error?.response?.data?.code;

      if (status === 409 && code === "E_DUPLICATE_ANSWER") {
        // Modal elegante avisando que ya estaba respondida
        await modalQuestionResolution({
          correct: false, // o false; aquí solo uso el modal para informar
          question: "Respuesta ya registrada",
          textHtml: `<div class="txt32 txtBlue bold">Esta pregunta ya se había contestado con anterioridad y no se contabilizará, pueden continuar con el resto del juego.</div>`,
          customConfirmButtonHtml: "Ir a la conclusión",
        });

        navigate(ROUTES.SCENARIO3_FIN);
        return;
      }

      // Cualquier otro error
      await modalQuestionResolution({
        correct: false,
        question: "No se pudo guardar",
        textHtml: `<div class="txt24 txt-red bold">Ha ocurrido un problema al registrar tu respuesta. Inténtalo de nuevo</div>`,
        customConfirmButtonHtml: "Aceptar",
      });

      setIsSubmitting(false);
    }
  }

  const useHint = async () => {
    const hint = `<img src=${imgPistaQuiz4} alt="Pista visual" style="width:80%;" />`;

    if (hintUsed) {
      // Si ya se usó, abre la pista directamente (sin confirmar)
      await modalHintShow({ hint, imageHintUsed, color: "violet", big: true });
      return;
    }

    // Primera vez: confirmar, mostrar pista y aplicar penalización
    const { used } = await modalHint({ image: imageHint, imageHintUsed, hint, color: "violet", big: true });
    if (used && !hintUsed) {
      setHintUsed(true);
      setHintPenalty(3); // restaremos 3 puntos al enviar la respuesta
    }
  };

  function applyHintOnceTo(scoreSoFar) {
    if (hintUsed && !hintAppliedToScore) {
      setHintAppliedToScore(true);
      return scoreSoFar - hintPenalty; // aplica -3 una sola vez
    }
    return scoreSoFar;
  }

  function checkAnswer() {
    if (!selectedOption) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<img src=${estasseguro} alt="¿Estás seguro?" class='estasseguro' /><div class="text-center txtTitulos mt-3 violet">Deben seleccionar una opción</div>`,
      });
      return;
    }

    const option = displayOptions.find((o) => o.id === selectedOption);
    if (!option) return;

    if (blockedOptions.has(option.id)) {
      modalQuestionResolution({
        correct: false,
        textHtml: `<div class="txt24 txt-red bold">Esa opción ya la intentaste y era incorrecta. Elijan otra.</div>`,
        customConfirmButtonHtml: "Aceptar",
      });
      return;
    }

    modalConfirm({ estasseguro, color: "violet" }).then((res) => {
      if (!res.isConfirmed) return;
      setIsSubmitting(true);

      // ✅ Correcta
      if (option.correct) {
        const base = (attempt === 1) ? CORRECT_ANSWER_POINTS : CORRECT_ANSWER_POINTS_2;
        const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;
        const attemptDelta = base - hintThisAttempt; // lo que realmente gana en este intento
        let newScore = pendingScore + attemptDelta;

        if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: true,
          question: option.text,
          textHtml: `${buildQuizResultAvatarHtml(respuestacorrecta, "correct", "violet")}
                      <div class="txt28 bold pt-4">Ganaron ${attemptDelta} puntos en este intento${hintThisAttempt ? " (-3 pista)" : ""}</div>
                      ${attempt === 2 ? `<div class="txt20 mt-2">Correcto en el segundo intento</div>` : ""}
                      <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                      ${retroalimentacionBlockHtml}`,
          customConfirmButtonHtml: "Continuar",
          color: "violet"
        }).then(() => {
          submitAnswer(newScore, true); // único envío
        });

        return;
      }

      // ❌ Incorrecta
      if (attempt < NUMBERATTEMPTS) {
        // 1er fallo: acumulamos -5, mostramos modal y seguimos SIN enviar aún
        const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;
        const attemptDelta = WRONG_ANSWER_POINTS - hintThisAttempt; // -5 o -8 si hay pista aquí
        let newScore = pendingScore + attemptDelta;
        if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

        modalQuestionResolution({
          correct: false,
          question: option.text,
          textHtml: `${buildQuizResultAvatarHtml(respuestaincorrecta, "incorrect", "violet")}
                      <div class="txt28 bold pt-3">Respuesta incorrecta ${attemptDelta} puntos${hintThisAttempt ? " (incluye -3 por pista)" : ""}</div>
                      <div class="txt22 mt-2">Les queda <b>1</b> intento. Elijan otra opción.</div>
                      <div class="txt20 mt-2">Acumulado hasta ahora: <b>${newScore}</b></div>`,
          customConfirmButtonHtml: "Intentar de nuevo",
          color: "violet"
        }).then(() => {
          setPendingScore(newScore);
          setBlockedOptions(prev => new Set(prev).add(option.id));
          setAttempt(attempt + 1);
          setSelectedOption(null);
          setIsSubmitting(false);
        });

        return;
      }

      // 2º fallo: suma +0 y enviamos el total final
      const hintThisAttempt = (hintUsed && !hintAppliedToScore) ? hintPenalty : 0;
      const attemptDelta = WRONG_ANSWER_POINTS_2 - hintThisAttempt; // 0 o -3
      let newScore = pendingScore + attemptDelta;
      if (hintUsed && !hintAppliedToScore) setHintAppliedToScore(true);

      modalQuestionResolution({
        correct: false,
        question: option.text,
        textHtml: `${buildQuizResultAvatarHtml(respuestaincorrecta, "incorrect", "violet")}
                    <div class="txt28 bold pt-4">Han agotado los intentos</div>
                    ${hintThisAttempt ? `<div class="txt20 mt-2">Se aplicó -3 por pista en este intento</div>` : ""}
                    <div class="txt20 mt-2">Puntuación total de la pregunta: <b>${newScore}</b></div>
                    ${retroalimentacionBlockHtml}`,
        customConfirmButtonHtml: "Continuar",
        color: "violet"
      }).then(() => {
        submitAnswer(newScore, false); // único envío
      });
    });
  }

  const remainingAttempts = NUMBERATTEMPTS - attempt + 1;
    
  return (
    <div className="h-100">
		  <div className="container-fluid px-5 h-100 d-flex flex-column justify-content-start pt-5 bgCircleViolet">
        <h3 className="txt24 txt-white text-center" style={{ backgroundColor: "#8c3888", borderRadius: "10px", padding: "20px"}}>
          ¿Qué prueba adicional es imprescindible para establecer el diagnóstico definitivo e iniciar la estrategia terapéutica del paciente?
        </h3>
        <div className="d-flex justify-content-between">
          <h5 className="mb-4 txt24 text-left my-2 gray">
            <IconHandClick size={40} color="#757575" stroke={1} /> Seleccionen la respuesta correcta
          </h5>
          <div className="mb-3">
            <img src={remainingAttempts == 2 ? dosIntentos : unIntento} className="imgIntentos" />
          </div>
        </div>
        <div className="row g-4 mb-5">
          {displayOptions.map((option) => {
            const isSelected = selectedOption === option.id;
            const isBlocked = blockedOptions.has(option.id);
            return (
              <div key={option.id} className="col-md-6">
                <div
                  className={`cursor-pointer p-3 pb-4 rounded shadow h-100 ${isSelected ? "selected" : ""} ${isBlocked ? "opacity-75" : ""}`}
                  style={{
                    border: isSelected ? "1px solid #8c388855" : "1px solid #fff",
                    backgroundColor: isSelected ? "#8c388855" : "#fff",
                    pointerEvents: isBlocked ? "none" : "auto",
                  }}
                  onClick={() => !isBlocked && setSelectedOption(option.id)}
                  aria-disabled={isBlocked}
                  title={isBlocked ? "Opción bloqueada (ya fue incorrecta)" : option.text}
                >
                  {option.img != null && <img
                    src={option.img}
                    alt={option.text}
                    className="mb-2"
                    style={{ width: "100%", borderRadius: "8px", height: "80%", objectFit: "cover" }}
                  />}
                  <div className="d-flex align-items-center justify-content-between">
                    <div className="txtTexto1 text-center gray">{option.text}</div>
                    {isBlocked && <span className="badge bg-secondary ms-2">Bloqueada</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="d-flex justify-content-between mt-4">
          <div className="position-relative d-inline-block">
            <img
              src={hintUsed ? btnPistaViolet : btnPistaViolet}
              className={`btnPista mt-2 ${hintUsed ? "used" : ""}`}
              onClick={useHint}
              alt={hintUsed ? "Pista usada" : "Usar pista"}
              title={hintUsed ? "Pista usada (-3)" : "Usar pista (-3 puntos)"}
            />
            {hintUsed && (
              <>
                <span className="badge-penalty">-3</span>
              </>
            )}
          </div>
          <CheckButton
            text="Comprobar respuesta"
            onClick={checkAnswer}
            disabled={isCompleted || isSubmitting}
            color="violet"
            loading={isSubmitting}
          />
        </div>
      </div>

      <ScenarioBar activeScenario={activeScenario} />
    </div>
  );
}

export default injectIntl(Scenario3Quiz1);
